/**
 * Sizing & TCO Preview Step - Shows simplified preview of sizing and TCO calculations
 */

import { useWizardState } from '@/hooks/useWizardState';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowRight, 
  Users, 
  Cpu, 
  DollarSign, 
  TrendingDown, 
  Info,
  Calculator,
  Server
} from 'lucide-react';

export function SizingTcoPreviewStep() {
  const { state } = useWizardState();
  const navigate = useNavigate();

  const selectedModelsCount = state.modelSelections.selectedModelIds.length;
  const personasCount = state.personas.length;
  const totalUsers = state.environment.totalActiveUsersPerDay;

  const handleOpenSizing = () => {
    // Navigate to sizing page with wizard state
    navigate('/sizing');
  };

  const handleOpenTco = () => {
    // Navigate to TCO calculator with wizard state
    navigate('/cost-simulator');
  };

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          These are preliminary estimates based on your configuration. Click "View Full Details" for comprehensive analysis.
        </AlertDescription>
      </Alert>

      {/* Sizing Preview */}
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-background">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5 text-primary" />
                Infrastructure Sizing
              </CardTitle>
              <CardDescription>
                GPU requirements and server configuration estimates
              </CardDescription>
            </div>
            <Button onClick={handleOpenSizing} variant="outline" size="sm">
              View Full Details
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-background/50 border border-border/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                Concurrent Users
              </div>
              <div className="text-2xl font-bold">{totalUsers.toLocaleString()}</div>
            </div>
            <div className="p-4 rounded-lg bg-background/50 border border-border/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Cpu className="h-4 w-4" />
                Models Selected
              </div>
              <div className="text-2xl font-bold">{selectedModelsCount}</div>
            </div>
            <div className="p-4 rounded-lg bg-background/50 border border-border/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                Personas
              </div>
              <div className="text-2xl font-bold">{personasCount}</div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-muted/50">
            <div className="flex items-start gap-2">
              <Calculator className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium mb-1">Estimated GPU Requirements</p>
                <p className="text-xs text-muted-foreground">
                  Full sizing calculations will determine the optimal number of GPUs, server SKUs, 
                  and configuration based on your workload patterns. Click "View Full Details" to run the complete analysis.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TCO Preview */}
      <Card className="border-2 border-accent/20 bg-gradient-to-br from-accent/5 to-background">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-accent" />
                Total Cost of Ownership
              </CardTitle>
              <CardDescription>
                5-year cost comparison: On-premises vs Cloud
              </CardDescription>
            </div>
            <Button onClick={handleOpenTco} variant="outline" size="sm">
              View Full Details
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-background/50 border border-border/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Server className="h-4 w-4" />
                On-Premises
              </div>
              <div className="text-2xl font-bold mb-1">TBD</div>
              <p className="text-xs text-muted-foreground">
                Based on hardware, power, cooling, and maintenance costs
              </p>
            </div>
            <div className="p-4 rounded-lg bg-background/50 border border-border/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Cpu className="h-4 w-4" />
                Cloud Hosting
              </div>
              <div className="text-2xl font-bold mb-1">TBD</div>
              <p className="text-xs text-muted-foreground">
                Based on instance pricing and usage patterns
              </p>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-muted/50">
            <div className="flex items-start gap-2">
              <TrendingDown className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium mb-1">Break-Even Analysis</p>
                <p className="text-xs text-muted-foreground">
                  The full TCO calculator will show when on-premises infrastructure pays for itself 
                  compared to cloud hosting, considering all factors including scale, utilization, and growth.
                </p>
              </div>
            </div>
          </div>

          <Badge variant="secondary" className="w-fit">
            <Info className="h-3 w-3 mr-1" />
            Detailed calculations available in full view
          </Badge>
        </CardContent>
      </Card>

      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="text-center text-sm text-muted-foreground">
            <p className="mb-2">
              These previews provide a high-level overview of your infrastructure needs and costs.
            </p>
            <p>
              For detailed analysis, hardware recommendations, and comprehensive TCO breakdowns, 
              click the "View Full Details" buttons above.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
